import React, {useEffect, useState} from "react";
import {TrainingRecord, TrainingRecordRestControllerApiFactory} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import FormFile from "../../../../components/form/form_inputs/form_file";
import {Box, Grid, Typography} from "@mui/material";
import {SoftChip} from "../../../../components/kit";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import {types} from "../../../../components/form/types";

/**
 * Una acción formativa o de concienciación de una persona (#13719). En la concienciación, además, el
 * resultado de la comprobación de cinco preguntas (#13901): superada con cuatro aciertos o más.
 */
export const TrainingRecordEditPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(TrainingRecordRestControllerApiFactory);
	const [kind, setKind] = useState<string>("FORMACION");
	const [passed, setPassed] = useState<boolean | undefined>();

	const form = useForm({
		fetcher: (id) => ctrl.getTrainingRecord(id),
		updater: (bean) => ctrl.saveTrainingRecord(bean),
		remover: (id) => ctrl.deleteOrDismissTrainingRecord(id),
		factory: () => ({
			kind: "FORMACION",
			status: "REALIZADA",
			trainingDate: new Date().toISOString().substring(0, 10),
		} as TrainingRecord),
		postLoad: (bean: TrainingRecord) => {
			setKind(bean.kind || "FORMACION");
			setPassed(bean.checkPassed);
		},
		// Si está superada lo calcula el servidor al guardar; se enseña lo que vuelve
		postUpdate: (bean: TrainingRecord) => setTimeout(() => {
			setPassed(bean.checkPassed);
			form.triggerField("checkDate", bean.checkDate);
		}, 0),
	} as UseFormProps<TrainingRecord>);

	useEffect(() => form.onFieldChange("kind", (v) => setKind(v)), []);

	return (
		<FormContent form={form} title={"Formación"}>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={4}>
					<FormAutocomplete field={"person"} label={"Persona"} type={types.people}/>
				</Grid>
				<Grid item xs={12} sm={2}>
					<FormSelect field={"kind"} label={"Tipo"} type={types.trainingKinds}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormSelect field={"area"} label={"Materia"} type={types.trainingAreas}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormSelect field={"status"} label={"Estado"} type={types.trainingStatuses}/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<FormInput field={"topic"} label={"Tema"}/>
				</Grid>
				<Grid item xs={12} sm={2}>
					<FormInput field={"trainingDate"} type={"date"} label={"Fecha"}/>
				</Grid>
				<Grid item xs={12} sm={1}>
					<FormInput field={"hours"} type={"number"} label={"Horas"}/>
				</Grid>
				<Grid item xs={12} sm={3}>
					<FormInput field={"provider"} label={"Impartida por"}/>
				</Grid>
			</Grid>
			{kind === "CONCIENCIACION" && (
				<Box sx={{mt: 2}}>
					<Typography variant="subtitle2" fontWeight={700}>Comprobación de lo aprendido</Typography>
					<Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
						Las cinco preguntas al terminar la acogida o el recordatorio anual. Se supera con cuatro aciertos o más.
					</Typography>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={6} sm={2}>
							<FormInput field={"checkCorrect"} type={"number"} label={"Aciertos"}/>
						</Grid>
						<Grid item xs={6} sm={2}>
							<FormInput field={"checkTotal"} type={"number"} label={"De"}/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<FormInput field={"checkDate"} type={"date"} label={"Fecha de la comprobación"}/>
						</Grid>
						<Grid item xs={12} sm={5}>
							{passed === undefined || passed === null
								? <SoftChip tone="neutral" label="Sin comprobación registrada"/>
								: <SoftChip tone={passed ? "success" : "error"} label={passed ? "Superada" : "No superada"}/>}
						</Grid>
					</Grid>
				</Box>
			)}
			<FormInput field={"notes"} label={"Notas"} rows={2}/>
			<FormFile field={"attachment"} label={"Evidencia (certificado, hoja de firmas)"}
					  onDownload={() => apiManager.executeRaw(ctrl.viewTrainingRecord(form.getIdOrErr()))}/>
		</FormContent>
	);
};
